import React, { useState, useRef, useEffect, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Dropdown } from 'react-bootstrap';
import {
  FiSearch,
  FiBell,
  FiMail,
  FiUser,
  FiSettings,
  FiLogOut,
  FiMenu,
  FiX,
  FiChevronsLeft,
  FiChevronsRight
} from 'react-icons/fi';
import Swal from 'sweetalert2';
import axios from 'axios';
import MyContext from '../../../context/MyContext';
import { getEcho } from '../../../components/general/echo/Echo';
import '../../../assets/css/components/admin/topbar/AdminTopbar.css';

const getTimeAgo = (dateString) => {
  const seconds = Math.floor((new Date() - new Date(dateString)) / 1000);
  if (seconds < 60) return 'Just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
  const days = Math.floor(hours / 24);
  return `${days} day${days > 1 ? 's' : ''} ago`;
};

// Notification "unread" ki definition robust ban chuki hai — status ki value
// database mein 'unread', 'Unread', ya kabhi kabhi garbage (0, null) bhi ho
// sakti hai. Is liye "read" ke elawa sab kuch unread mana jata hai.
const isUnread = (status) => String(status).toLowerCase() !== 'read';

const AdminTopbar = ({ isCollapsed, setIsCollapsed, isMobileOpen, setIsMobileOpen, isMobile }) => {
  const navigate = useNavigate();
  const { baseUrl, loggedInAdmin, setLoggedInAdmin, authToken } = useContext(MyContext);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showMessages, setShowMessages] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const notificationRef = useRef(null);
  const messageRef = useRef(null);

  const [messages, setMessages] = useState([]);

  const fetchNotifications = () => {
    axios.get(`${baseUrl}/admin/notifications/viewAllNotifications`, {
      headers: {
        Authorization: `Bearer ${authToken}`
      }
    })
      .then((response) => {
        if (response.data.success) {
          setNotifications(response.data.notifications);
        }
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const fetchMessages = () => {
    axios.get(`${baseUrl}/admin/contact/viewAllMessages`, {
      headers: {
        Authorization: `Bearer ${authToken}`
      }
    })
      .then((response) => {
        if (response.data.success) {
          setMessages(response.data.messages);
        }
      })
      .catch((error) => {
        console.log(error);
      });
  };

  // ── NAYA: page reload par loggedInAdmin khaali reh jata tha (Context
  // localStorage mein persist nahi hoti, sirf authToken hoti hai). Is liye
  // agar token hai lekin admin ka data Context mein nahi hai, to profile
  // dobara fetch kar ke Context mein wapis daal dete hain. ──
  const fetchAdminProfile = () => {
    axios.get(`${baseUrl}/admin/profile/viewAdminProfile`, {}, {
      headers: {
        Authorization: `Bearer ${authToken}`
      }
    })
      .then((response) => {
        if (response.data.success) {
          setLoggedInAdmin(response.data.admin);
        }
      })
      .catch((error) => {
        console.log(error);
      });
  };

  useEffect(() => {
    if (!authToken) return;
    fetchNotifications();
    fetchMessages();
    if (!loggedInAdmin) {
      fetchAdminProfile();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authToken]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (notificationRef.current && !notificationRef.current.contains(e.target)) setShowNotifications(false);
      if (messageRef.current && !messageRef.current.contains(e.target)) setShowMessages(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ── Real-time notifications (Pusher/Echo) ──
  // NewNotification event backend se 'admin.{id}' private channel par
  // broadcast hota hai (recipientType = 'admin', lowercase). Jaise hi
  // naya notification aaye, list mein turant upar add kar dete hain —
  // page refresh ki zaroorat nahi.
  useEffect(() => {
    if (!authToken || !loggedInAdmin?.id) return;

    const echo = getEcho(baseUrl, authToken);
    if (!echo) return;

    const channelName = `admin.${loggedInAdmin.id}`;
    const channel = echo.private(channelName);

    channel.listen('.new-notification', (data) => {
      if (data?.notification) {
        setNotifications((prev) => [data.notification, ...prev]);
      }
    });

    return () => {
      echo.leave(channelName);
    };
  }, [authToken, loggedInAdmin?.id, baseUrl]);


  // ── notification pe click: mark as read + notifications page
  // par navigate karo us particular notification ke sath ──
  const openNotification = (n) => {
    setShowNotifications(false);

    if (isUnread(n.status)) {
      const formData = new FormData();
      formData.append('id', n.id);

      axios
        .post(`${baseUrl}/admin/notifications/changeNotificationStatus`, formData, {
          headers: { Authorization: `Bearer ${authToken}` },
        })
        .then(() => {
          setNotifications((prev) =>
            prev.map((item) =>
              item.id === n.id ? { ...item, status: 'read' } : item,
            ),
          );
        })
        .catch((error) => console.log(error));
    }

    navigate('/admin/notifications', { state: { openNotification: n } });
  };


  const handleLogout = () => {
    Swal.fire({
      title: 'Are you sure?',
      text: 'Do you want to logout?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, Logout',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#097d43',
      cancelButtonColor: '#dc3545',
    }).then((result) => {
      if (result.isConfirmed) {
        Swal.fire({
          title: 'Logged Out!',
          text: 'You have been logged out successfully.',
          icon: 'success',
          confirmButtonColor: '#097d43',
          timer: 2000
        }).then(() => navigate('/'));
      }
    });
  };

  const handleToggleSidebar = () => {
    if (isMobile) {
      setIsMobileOpen(!isMobileOpen);
    } else {
      setIsCollapsed(!isCollapsed);
    }
  };

  const unreadNotifications = notifications.filter(n => isUnread(n.status)).length;
  const unreadMessages      = messages.filter(m => !m.reply).length;

  return (
    <div className="adm-topbar">

      {/* Toggle Button */}
      <button className="adm-topbar__toggle" onClick={handleToggleSidebar}>
        {isMobile ? (
          isMobileOpen ? <FiX /> : <FiMenu />
        ) : (
          isCollapsed ? <FiChevronsRight /> : <FiChevronsLeft />
        )}
      </button>

      {/* Right Actions */}
      <div className="adm-topbar__actions">

        {/* Messages */}
        <div className="adm-topbar__dropdown-wrap" ref={messageRef}>
          <button
            className="adm-topbar__icon-btn"
            onClick={() => { setShowMessages(!showMessages); setShowNotifications(false); }}
          >
            <FiMail />
            {unreadMessages > 0 && (
              <span className="adm-topbar__badge">{unreadMessages}</span>
            )}
          </button>

          {showMessages && (
            <div className="adm-topbar__dropdown">
              <div className="adm-topbar__dropdown-header">
                <h6>Messages</h6>
                <button className="adm-topbar__close-btn" onClick={() => setShowMessages(false)}>
                  <FiX />
                </button>
              </div>
              <div className="adm-topbar__dropdown-body">
                {messages.map((msg) => (
                  <div
                    key={msg.id}
                    className={`adm-topbar__dropdown-item ${!msg.reply ? 'adm-topbar__dropdown-item--unread' : ''}`}
                    onClick={() => navigate(`/admin/messages?contact=${msg.id}`)}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="adm-topbar__msg-avatar">{msg.name?.charAt(0)}</div>
                    <div className="adm-topbar__msg-content">
                      <h6>{msg.name}</h6>
                      <p>{msg.subject || msg.message}</p>
                      <span>{getTimeAgo(msg.created_at)}</span>
                    </div>
                  </div>
                ))}
              </div>
              <div className="adm-topbar__dropdown-footer">
                <Link to="/admin/messages">View All Messages</Link>
              </div>
            </div>
          )}
        </div>

        {/* Notifications */}
        <div className="adm-topbar__dropdown-wrap" ref={notificationRef}>
          <button
            className="adm-topbar__icon-btn"
            onClick={() => { setShowNotifications(!showNotifications); setShowMessages(false); }}
          >
            <FiBell />
            {unreadNotifications > 0 && (
              <span className="adm-topbar__badge">{unreadNotifications}</span>
            )}
          </button>

          {showNotifications && (
            <div className="adm-topbar__dropdown">
              <div className="adm-topbar__dropdown-header">
                <h6>Notifications</h6>
                <button className="adm-topbar__close-btn" onClick={() => setShowNotifications(false)}>
                  <FiX />
                </button>
              </div>
              <div className="adm-topbar__dropdown-body">
                {notifications.map((notif) => (
                  <div
                    key={notif.id}
                    className={`adm-topbar__dropdown-item ${isUnread(notif.status) ? 'adm-topbar__dropdown-item--unread' : ''}`}
                    onClick={() => openNotification(notif)}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="adm-topbar__notif-dot"></div>
                    <div className="adm-topbar__notif-content">
                      <h6>{notif.title}</h6>
                      <p>{notif.message}</p>
                      <span>{getTimeAgo(notif.created_at)}</span>
                    </div>
                  </div>
                ))}
              </div>
              <div className="adm-topbar__dropdown-footer">
                <Link to="/admin/notifications">View All Notifications</Link>
              </div>
            </div>
          )}
        </div>

        {/* Profile - name & role only, no avatar */}
        <Dropdown>
          <Dropdown.Toggle as="div" className="adm-topbar__profile">
            <div className="adm-topbar__profile-info">
              {/* name hidden on mobile via React */}
              {!isMobile && <h6>{loggedInAdmin?.name}</h6>}
              <p>ADMIN</p>
            </div>
          </Dropdown.Toggle>

          <Dropdown.Menu align="end">
            <Dropdown.Item as={Link} to="/admin/settings">
              <FiUser /> My Profile
            </Dropdown.Item>
            <Dropdown.Item as={Link} to="/admin/settings">
              <FiSettings /> Settings
            </Dropdown.Item>
            <Dropdown.Divider />
            <Dropdown.Item onClick={handleLogout} className="adm-topbar__logout-item">
              <FiLogOut /> Logout
            </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>

      </div>
    </div>
  );
};

export default AdminTopbar;
